import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  Avatar,
  Button,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Pill,
  DataTable,
  SegmentedControl,
  Select,
  Sheet,
  Skeleton,
  SkeletonText,
  useToast,
} from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Person } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { usePersistentState } from "../lib/use-persistent-state.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { localTimeIn } from "../lib/local-time.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { StatusMark } from "../components/StatusMark.js";
import "./people.css";

/* ─────────────────────────────── local types ─────────────────────────────── */

/**
 * The shared `Person` type in lib/api.ts doesn't declare `avatarUrl` yet even
 * though the API returns it — extended locally rather than editing that
 * shared file, which is out of scope for this surface (same approach Work.tsx
 * takes with `Task`).
 */
type DirectoryPerson = Person & {
  avatarUrl?: string | null;
  /** Only on the detail endpoint. */
  phone?: string | null;
  timezone?: string | null;
  manager?: { id: string; displayName: string } | null;
};

/**
 * Projects and the open-task count only come back from the detail endpoint —
 * declared here rather than folded into the shared `Person` type so the
 * directory list (which never fetches this) doesn't imply it has them.
 */
type PersonDetail = {
  person: DirectoryPerson;
  projects: { id: string; code: string; name: string; status: string; health: string; memberRole: string | null }[];
  openTaskCount: number;
};

const AVAILABILITY_TONE: Record<string, PillTone> = {
  available: "ok",
  busy: "warn",
  away: "neutral",
  offline: "neutral",
  do_not_disturb: "danger",
};

const AVAILABILITY_LABEL: Record<string, string> = {
  available: "Available",
  busy: "Busy",
  away: "Away",
  offline: "Offline",
  do_not_disturb: "Do not disturb",
};

const HEALTH_TONE: Record<string, PillTone> = {
  on_track: "ok",
  at_risk: "warn",
  off_track: "danger",
};

function availabilityLabel(value: string): string {
  return AVAILABILITY_LABEL[value] ?? value.replace(/_/g, " ");
}

function exportPeopleCsv(rows: DirectoryPerson[]): void {
  const csv = toCsv(
    ["Name", "Email", "Job title", "Department", "Role", "Availability"],
    rows,
    (person) => [
      person.displayName,
      person.email,
      person.jobTitle ?? "",
      person.department ?? "",
      person.role,
      availabilityLabel(person.availability),
    ],
  );
  downloadCsv(`people-${new Date().toLocaleDateString("en-CA")}.csv`, csv);
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < 5 * minute) return "Active now";
  if (diffMs < hour) return `Active ${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `Active ${Math.floor(diffMs / hour)}h ago`;
  return `Active ${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/* ──────────────────────────────────── filter bar ──────────────────────────────────── */

interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  department: string;
  onDepartmentChange: (value: string) => void;
  departments: string[];
  view: "cards" | "list";
  onViewChange: (view: "cards" | "list") => void;
}

function FilterBar({
  search,
  onSearchChange,
  department,
  onDepartmentChange,
  departments,
  view,
  onViewChange,
}: FilterBarProps) {
  // Local echo so typing feels instant while the debounced value (owned by
  // the parent, and reflected into the URL) drives the actual fetch.
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  return (
    <div className="people-filterbar">
      <label className="people-filterbar__search">
        <Icon name="search" size={14} />
        <input
          type="search"
          placeholder="Search people…"
          value={searchDraft}
          onChange={(e) => {
            const value = e.target.value;
            setSearchDraft(value);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => onSearchChange(value), 250);
          }}
          aria-label="Search people"
        />
      </label>

      <Select
        aria-label="Filter by department"
        value={department}
        onChange={(e) => onDepartmentChange(e.target.value)}
        className="people-filterbar__select"
      >
        <option value="">All departments</option>
        {departments.map((dept) => (
          <option key={dept} value={dept}>
            {dept}
          </option>
        ))}
      </Select>

      <div className="people-filterbar__spacer" />

      <SegmentedControl
        aria-label="View"
        value={view}
        onChange={(value) => onViewChange(value === "list" ? "list" : "cards")}
        options={[
          { value: "cards", label: "Cards" },
          { value: "list", label: "List" },
        ]}
      />
    </div>
  );
}

/* ──────────────────────────────────── person card ──────────────────────────────────── */

interface PersonCardProps {
  person: DirectoryPerson;
  active: boolean;
  onSelect: (person: DirectoryPerson) => void;
}

function PersonCard({ person, active, onSelect }: PersonCardProps) {
  return (
    <button type="button" className={`people-card ${active ? "is-active" : ""}`} onClick={() => onSelect(person)}>
      <span className="people-card__avatar-wrap">
        <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={44} />
        <span
          className={`people-card__dot people-card__dot--${person.availability}`}
          aria-label={availabilityLabel(person.availability)}
          title={availabilityLabel(person.availability)}
        />
      </span>
      <span className="people-card__name">
        {person.displayName}
        <StatusMark userId={person.id} fetched={person.status} />
      </span>
      {person.jobTitle && <span className="people-card__title">{person.jobTitle}</span>}
      {person.department && (
        <Pill tone="neutral" className="people-card__department">
          {person.department}
        </Pill>
      )}
    </button>
  );
}

/* ──────────────────────────────────── detail panel ──────────────────────────────────── */

interface DetailPanelProps {
  detail: PersonDetail | undefined;
  loading: boolean;
}

function DetailPanelContent({ detail, loading }: DetailPanelProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [opening, setOpening] = useState(false);

  const message = useCallback(
    (personId: string) => {
      setOpening(true);
      // Idempotent server-side, so this lands in the existing thread if there
      // already is one rather than starting a second.
      api
        .post<{ channel: { id: string } }>("/channels/direct", { userId: personId })
        .then((res) => navigate(`/chat/${res.channel.id}`))
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not open that conversation.", {
            tone: "danger",
          });
        })
        .finally(() => setOpening(false));
    },
    [navigate, toast],
  );

  if (loading) {
    return (
      <div className="people-detail__section">
        <SkeletonText lines={4} />
      </div>
    );
  }
  if (!detail) return null;

  const { person, projects, openTaskCount } = detail;
  const localTime = localTimeIn(person.timezone);

  return (
    <>
      <div className="people-detail__profile">
        <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={64} />
        <h3>{person.displayName}</h3>
        <StatusMark userId={person.id} fetched={person.status} showText />
        {person.jobTitle && <p className="people-detail__job-title">{person.jobTitle}</p>}
        <div className="people-detail__pills">
          {person.department && <Pill tone="neutral">{person.department}</Pill>}
          <Pill tone={AVAILABILITY_TONE[person.availability] ?? "neutral"} dot>
            {availabilityLabel(person.availability)}
          </Pill>
        </div>

        {/* Hidden on your own card — there is no conversation with yourself. */}
        {person.id !== viewer?.id && (
          <Button
            size="sm"
            className="people-detail__message"
            icon={<Icon name="chat" size={14} />}
            loading={opening}
            onClick={() => message(person.id)}
          >
            Message
          </Button>
        )}
      </div>

      <div className="people-detail__section">
        <h4>Contact</h4>
        <a className="people-detail__email" href={`mailto:${person.email}`}>
          <Icon name="link" size={13} />
          {person.email}
        </a>
        {person.phone && (
          <a className="people-detail__email" href={`tel:${person.phone.replace(/[^+\d]/g, "")}`}>
            <Icon name="chat" size={13} />
            {person.phone}
          </a>
        )}
        {localTime && (
          <p className="people-detail__last-seen">
            <Icon name="clock" size={13} /> {localTime} local time
          </p>
        )}
        {person.manager && (
          <p className="people-detail__last-seen">
            Reports to{" "}
            <Link to={`/people?person=${person.manager.id}`}>{person.manager.displayName}</Link>
          </p>
        )}
        {person.lastSeenAt && <p className="people-detail__last-seen">{formatLastSeen(person.lastSeenAt)}</p>}
      </div>

      <div className="people-detail__section">
        <h4>Open tasks · {openTaskCount}</h4>
      </div>

      <div className="people-detail__section">
        <h4>Projects · {projects.length}</h4>
        {projects.length === 0 ? (
          <p className="people-detail__empty-hint">Not a member of any project yet.</p>
        ) : (
          <div className="people-detail__projects">
            {projects.map((project) => (
              <div key={project.id} className="people-detail__project">
                <span className="people-detail__project-code">{project.code}</span>
                <span className="people-detail__project-name">{project.name}</span>
                <Pill tone={HEALTH_TONE[project.health] ?? "neutral"}>{project.health.replace("_", " ")}</Pill>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ──────────────────────────────────── main view ──────────────────────────────────── */

export function People() {
  usePageTitle("People");
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { viewer } = useAuth();
  const navigate = useNavigate();

  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(true);
  /** The list is empty because the request failed, not because nobody is here. */
  const [loadFailed, setLoadFailed] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);

  const [detail, setDetail] = useState<PersonDetail | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // URL is the single source of truth for search + filter, same convention as Work.
  const search = searchParams.get("q") ?? "";
  const department = searchParams.get("department") ?? "";
  /*
   * Cards or a list.
   *
   * Cards are a face-first directory and read well for a small team; they stop
   * working once there are enough people to scroll, and they hide the email,
   * role and last-seen that are already fetched.
   *
   * The URL is the shareable source of truth — a link with `?view=list` opens
   * that way — but the choice is also remembered per browser, so returning to
   * People later (with no query string) reopens the layout last used rather than
   * snapping back to cards. The URL wins when it names a view; the remembered
   * value fills in when it does not.
   */
  const [rememberedView, rememberView] = usePersistentState<"cards" | "list">(
    "people.view",
    "cards",
    ["cards", "list"],
  );
  const view = searchParams.get("view") === "list" ? "list" : searchParams.get("view") === "cards" ? "cards" : rememberedView;
  const selectedId = searchParams.get("person") ?? undefined;

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /* ── load the department list once ── */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ departments: string[] }>("/people/departments")
      .then((res) => {
        if (!cancelled) setDepartments(res.departments);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── load the directory whenever search or department changes ── */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setPeopleLoading(true);

      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (department) params.set("department", department);

      try {
        const res = await api.get<{ people: DirectoryPerson[] }>(`/people?${params.toString()}`);
        if (cancelled) return;
        setPeople(res.people);
        setLoadFailed(false);
      } catch (error) {
        if (cancelled) return;
        /*
         * A failure is remembered, not just toasted: an empty list would
         * otherwise read as "no people yet", asserting something false about
         * the company.
         */
        setLoadFailed(true);
        toast(error instanceof ApiError ? error.message : "Could not load people.", { tone: "danger" });
      } finally {
        if (!cancelled) setPeopleLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, department, viewer]);

  /* ── load a person's detail whenever the selection changes ── */
  useEffect(() => {
    if (!selectedId) {
      setDetail(undefined);
      setSheetOpen(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setSheetOpen(true);

    api
      .get<PersonDetail>(`/people/${selectedId}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load that person.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleSelect = useCallback(
    (person: DirectoryPerson) => {
      updateParams({ person: person.id === selectedId ? undefined : person.id });
    },
    [updateParams, selectedId],
  );

  const handleClosePanel = useCallback(() => {
    updateParams({ person: undefined });
  }, [updateParams]);

  const isEmptyResult = !peopleLoading && people.length === 0;
  const hasActiveFilters = !!(search || department);

  const detailForSelected = useMemo(
    () => (detail && detail.person.id === selectedId ? detail : undefined),
    [detail, selectedId],
  );

  return (
    <div className="people-shell">
      <section className="people-main" aria-label="People directory">
        <PageHeader
          title="People"
          meta={peopleLoading ? undefined : `${people.length} ${people.length === 1 ? "person" : "people"}`}
          actions={
            <>
              <Button variant="secondary" size="sm" icon={<Icon name="people" size={14} />} onClick={() => navigate("/people/org-chart")}>
                Org chart
              </Button>
              {people.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Icon name="download" size={14} />}
                  onClick={() => exportPeopleCsv(people)}
                >
                  Export CSV
                </Button>
              )}
            </>
          }
        />

        <FilterBar
          search={search}
          onSearchChange={(value) => updateParams({ q: value || undefined })}
          department={department}
          onDepartmentChange={(value) => updateParams({ department: value || undefined })}
          departments={departments}
          view={view}
          onViewChange={(next) => {
            rememberView(next);
            // Write the view explicitly (both values), so the URL always states
            // the current choice rather than relying on "absent means cards".
            updateParams({ view: next });
          }}
        />

        <div className="people-content">
          {peopleLoading ? (
            <div className="people-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="people-card people-card--skeleton">
                  <Skeleton circle width={44} height={44} />
                  <SkeletonText lines={2} />
                </div>
              ))}
            </div>
          ) : loadFailed ? (
            <EmptyState
              icon="warning"
              title="Could not load the directory"
              description="The request was refused or did not arrive. Reload the page, and tell an administrator if it keeps happening."
            />
          ) : isEmptyResult ? (
            <EmptyState
              icon="people"
              title={hasActiveFilters ? "No one matches" : "No people yet"}
              description={hasActiveFilters ? "Try adjusting your search or department filter." : "People will show up here once they join the workspace."}
            />
          ) : view === "list" ? (
            /*
             * The columns the cards cannot show.
             *
             * Email, role and last-seen are already fetched and were being
             * thrown away — and a card whose person has no job title or
             * department is just a name in a box, which is most of them.
             */
            <DataTable
              caption="Everybody in the workspace"
              density="compact"
              rows={people}
              rowKey={(person) => person.id}
              onRowClick={handleSelect}
              columns={[
                {
                  key: "name",
                  header: "Name",
                  width: "260px",
                  sticky: true,
                  sortable: true,
                  render: (person) => (
                    <span className="people-list__person">
                      <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={24} />
                      <span className="people-list__name">{person.displayName}</span>
                      <StatusMark userId={person.id} fetched={person.status} />
                    </span>
                  ),
                },
                {
                  key: "email",
                  header: "Email",
                  render: (person) => <span className="people-list__muted">{person.email}</span>,
                },
                {
                  key: "jobTitle",
                  header: "Job title",
                  width: "180px",
                  render: (person) => (
                    <span className="people-list__muted">{person.jobTitle || "—"}</span>
                  ),
                },
                {
                  key: "department",
                  header: "Department",
                  width: "150px",
                  render: (person) =>
                    person.department ? <Pill tone="neutral">{person.department}</Pill> : <span className="people-list__muted">—</span>,
                },
                {
                  key: "availability",
                  header: "Status",
                  width: "130px",
                  render: (person) => (
                    <Pill tone={AVAILABILITY_TONE[person.availability] ?? "neutral"} dot>
                      {availabilityLabel(person.availability)}
                    </Pill>
                  ),
                },
                {
                  key: "lastSeen",
                  header: "Last seen",
                  width: "150px",
                  render: (person) => (
                    <span className="people-list__muted">{formatLastSeen(person.lastSeenAt) || "—"}</span>
                  ),
                },
              ]}
            />
          ) : (
            <div className="people-grid">
              {people.map((person) => (
                <PersonCard key={person.id} person={person} active={person.id === selectedId} onSelect={handleSelect} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Desktop: inline panel alongside the grid. Mobile: a Sheet triggered by
          the same selection state, matching Chat's details-panel split. */}
      <aside className={`people-detail ${selectedId ? "is-open" : ""}`} aria-label="Person details">
        {selectedId && (
          <div className="people-detail__header">
            <h3>Profile</h3>
            <IconButton icon={<Icon name="close" size={15} />} label="Close profile" onClick={handleClosePanel} />
          </div>
        )}
        {selectedId && (
          <div className="people-detail__scroll">
            <DetailPanelContent detail={detailForSelected} loading={detailLoading} />
          </div>
        )}
      </aside>

      <div className="people-detail--mobile">
        <Sheet open={sheetOpen} onClose={handleClosePanel} title="Profile">
          <DetailPanelContent detail={detailForSelected} loading={detailLoading} />
        </Sheet>
      </div>
    </div>
  );
}
